import { revealScrollDelta } from '../components/focus-reveal.js';
import { canAnimateElement, readCssTimeMs } from '../components/motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from '../components/motion-runner.js';

// Mirrors PHP 8.4 `mb_trim()` with its default character set, which `GameCrudService::saveReview()` applies
// before storing: space, \t \n \r \v \f \0, U+00A0, U+1680, U+2000-U+200A, U+2028, U+2029, U+202F, U+205F,
// U+3000, U+0085, U+180E (not U+FEFF). Every "is there something unsaved?" decision compares through it.
const REVIEW_TRIM_CHARACTERS = ' \\t\\n\\r\\v\\f\\x00\\u00A0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000\\u0085\\u180E';
const REVIEW_TRIM_PATTERN = new RegExp(`^[${REVIEW_TRIM_CHARACTERS}]+|[${REVIEW_TRIM_CHARACTERS}]+$`, 'g');

export function normalizeReview(text) {
    return String(text).replace(REVIEW_TRIM_PATTERN, '');
}

const SAVE_STATUS_TEXT = {
    idle: 'Changes save automatically',
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Save failed',
};

export function initGameDetailsReview(documentRef = document, fetchImpl = fetch, timerApi = globalThis) {
    const AUTO_SAVE_DELAY_MS = 3000;
    const SAVE_CONFIRMATION_MS = 350;
    const MOTION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

    const reviewSection = documentRef.querySelector('[data-game-review-section]');
    if (!(reviewSection instanceof HTMLElement)) {
        return null;
    }

    const reviewComposer = reviewSection.querySelector('[data-game-review-composer]');
    const reviewForm = reviewSection.querySelector('[data-game-review-form]');
    const reviewRead = reviewSection.querySelector('[data-game-review-read]');
    const reviewEmpty = reviewSection.querySelector('[data-game-review-empty]');
    const reviewTextarea = reviewSection.querySelector('[data-game-review-textarea]');
    const editButton = reviewSection.querySelector('[data-game-review-edit]');
    const startButtons = reviewSection.querySelectorAll('[data-game-review-start]');
    const doneButton = reviewSection.querySelector('[data-game-review-done]');
    const saveStatus = reviewSection.querySelector('[data-game-review-save-status]');
    const readToggle = reviewSection.querySelector('[data-game-review-toggle]');
    const collapseTopButton = reviewSection.querySelector('[data-game-review-collapse-top]');

    if (
        !(reviewComposer instanceof HTMLElement)
        || !(reviewForm instanceof HTMLFormElement)
        || !(reviewTextarea instanceof HTMLTextAreaElement)
    ) {
        return null;
    }

    const csrfTokenInput = reviewForm.querySelector('input[name="csrf_token"]');
    const reviewReadParagraph = reviewRead instanceof HTMLElement ? reviewRead.querySelector('p') : null;

    // What the server holds. Autosave records it from the response and never writes it into the field:
    // the field is the user's, with its trailing space or newline, until the edit closes.
    let confirmedContent = normalizeReview(reviewTextarea.defaultValue);
    let autoSaveTimerId = null;
    let activeSave = null;
    let autosaveDueDuringSave = false;
    let hasSavedThisEdit = false;
    let lastSaveFailed = false;
    let isEditMode = false;
    let isClosing = false;
    let isUnloadGuardArmed = false;
    let isTransitioning = false;
    let isExpanded = false;
    let isCollapseTopShown = false;
    let collapsedHeight = 0;

    // After a failed save the server may hold the version that was sent, so nothing counts as saved until a
    // save succeeds - even a text put back to the confirmed content.
    const hasUnsavedChanges = () => lastSaveFailed || normalizeReview(reviewTextarea.value) !== confirmedContent;

    const wait = (milliseconds) => new Promise((resolve) => {
        timerApi.setTimeout(resolve, milliseconds);
    });

    const clearAutoSaveTimer = () => {
        if (autoSaveTimerId === null) {
            return;
        }

        timerApi.clearTimeout(autoSaveTimerId);
        autoSaveTimerId = null;
    };

    const renderSaveStatus = (state) => {
        if (!(saveStatus instanceof HTMLElement) || saveStatus.dataset.state === state) {
            return;
        }

        saveStatus.dataset.state = state;
        saveStatus.textContent = SAVE_STATUS_TEXT[state];
    };

    // `Saved` only while the field holds what the server confirmed; `Save failed` until a save succeeds.
    const syncSaveStatus = () => {
        if (activeSave !== null) {
            renderSaveStatus('saving');
        } else if (lastSaveFailed) {
            renderSaveStatus('error');
        } else if (hasSavedThisEdit && !hasUnsavedChanges()) {
            renderSaveStatus('saved');
        } else {
            renderSaveStatus('idle');
        }
    };

    const preventUnload = (event) => {
        event.preventDefault();
        event.returnValue = '';
    };

    const syncUnloadGuard = () => {
        const view = documentRef.defaultView;
        const shouldArm = isEditMode && (activeSave !== null || hasUnsavedChanges());
        if (shouldArm === isUnloadGuardArmed) {
            return;
        }

        isUnloadGuardArmed = shouldArm;
        if (shouldArm) {
            view?.addEventListener?.('beforeunload', preventUnload);
        } else {
            view?.removeEventListener?.('beforeunload', preventUnload);
        }
    };

    const syncReadModeContent = () => {
        if (!(reviewReadParagraph instanceof HTMLElement)) {
            return;
        }

        reviewReadParagraph.textContent = confirmedContent;
    };

    const scheduleAutosave = () => {
        clearAutoSaveTimer();
        autoSaveTimerId = timerApi.setTimeout(() => {
            autoSaveTimerId = null;
            void autosaveReview();
        }, AUTO_SAVE_DELAY_MS);
    };

    const requestSave = async (contentToSave) => {
        if (!(csrfTokenInput instanceof HTMLInputElement) || csrfTokenInput.value.trim() === '') {
            throw new Error('Missing CSRF token.');
        }

        const response = await fetchImpl(reviewForm.action, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfTokenInput.value,
            },
            credentials: 'same-origin',
            body: JSON.stringify({ content: contentToSave }),
        });

        if (!response.ok) {
            throw new Error('Save failed.');
        }

        const payload = await response.json();
        if (
            typeof payload !== 'object'
            || payload === null
            || payload.success !== true
            || typeof payload.content !== 'string'
        ) {
            throw new Error('Save response is invalid.');
        }

        return payload.content;
    };

    // One save at a time, never aborted: a sent request may already be written on the server. The response
    // confirms the version that was sent, which is not necessarily what the field holds by then. The promise
    // settles after its own cleanup, so whoever awaits it sees the finished state, and a finished save only
    // clears its own slot - never a newer one.
    const saveCurrentText = () => {
        const save = requestSave(normalizeReview(reviewTextarea.value))
            .then(
                (savedContent) => {
                    confirmedContent = savedContent;
                    hasSavedThisEdit = true;
                    lastSaveFailed = false;

                    return true;
                },
                () => {
                    lastSaveFailed = true;

                    return false;
                },
            )
            .then((saveSucceeded) => {
                if (activeSave === save) {
                    activeSave = null;
                }

                // A change typed during the request goes right after it, so `Saving…` runs on without a gap:
                // one whose delay ran out in flight always, one whose timer still runs only after a success
                // (a failure is retried by the next change, never by itself).
                const dueDuringSave = autosaveDueDuringSave;
                autosaveDueDuringSave = false;
                const followUpDue = dueDuringSave || (saveSucceeded && autoSaveTimerId !== null);
                if (followUpDue && isEditMode && !isClosing && activeSave === null && hasUnsavedChanges()) {
                    clearAutoSaveTimer();
                    void saveCurrentText();
                } else {
                    syncSaveStatus();
                    syncUnloadGuard();
                }

                return saveSucceeded;
            });
        activeSave = save;
        syncSaveStatus();
        syncUnloadGuard();

        return save;
    };

    const autosaveReview = async () => {
        clearAutoSaveTimer();
        if (!isEditMode || isClosing) {
            return;
        }

        if (activeSave !== null) {
            autosaveDueDuringSave = true;
            return;
        }

        if (!hasUnsavedChanges()) {
            return;
        }

        await saveCurrentText();
    };

    const setExpandedState = (expanded) => {
        isExpanded = expanded;
        reviewRead?.classList.toggle('game-review-read-collapsed', !expanded);

        if (readToggle instanceof HTMLElement) {
            readToggle.textContent = expanded ? 'Show less' : 'Show more';
            readToggle.setAttribute('aria-expanded', String(expanded));
        }
    };

    // The toggle exists only when the clamped text is actually cut, or once it is expanded.
    const syncReadToggle = () => {
        if (!(readToggle instanceof HTMLElement) || !(reviewReadParagraph instanceof HTMLElement)) {
            return;
        }

        readToggle.hidden = !isExpanded && reviewReadParagraph.scrollHeight <= reviewReadParagraph.clientHeight + 1;
    };

    // A second "Show less" next to Edit, only while the expanded review is taller than the window.
    const syncCollapseTop = async () => {
        if (!(collapseTopButton instanceof HTMLElement) || !(reviewRead instanceof HTMLElement)) {
            return;
        }

        const viewportHeight = documentRef.defaultView?.innerHeight ?? Infinity;
        const shouldShow = isExpanded && !isEditMode && reviewRead.getBoundingClientRect().height > viewportHeight;
        if (shouldShow === isCollapseTopShown) {
            return;
        }

        isCollapseTopShown = shouldShow;
        collapseTopButton.hidden = false;
        if (canAnimateElement(collapseTopButton, documentRef.defaultView)) {
            const animation = playAnimation(collapseTopButton, [
                { opacity: 0, transform: 'translateX(6px)' },
                { opacity: 1, transform: 'translateX(0)' },
            ], { duration: 200, easing: MOTION_EASING, direction: shouldShow ? 'normal' : 'reverse', fill: 'both' });
            await settleAnimations([animation]);
            cancelAnimations([animation]);
        }

        collapseTopButton.hidden = !isCollapseTopShown;
    };

    const hideCollapseTop = () => {
        isCollapseTopShown = false;
        if (collapseTopButton instanceof HTMLElement) {
            collapseTopButton.hidden = true;
        }
    };

    // Collapse and the scroll back to the review run in one frame loop with one easing, so they move
    // together: the text rewinds under a nearly still bottom edge and the notes below do not jump.
    // Browser smooth scroll cannot be synchronised with a height animation, hence the manual loop.
    // The section's scroll-margin-top is the sticky header's height: the top of the visible area.
    const readHeaderOffset = () => Number.parseFloat(globalThis.getComputedStyle?.(reviewSection)?.scrollMarginTop) || 0;

    const collapseWithScroll = (fromHeight, scrollMargin) => new Promise((resolve) => {
        const view = documentRef.defaultView;
        const rootStyle = documentRef.documentElement.style;
        const fromScroll = view.scrollY;
        const toScroll = fromScroll + reviewSection.getBoundingClientRect().top - scrollMargin;
        const duration = 600;
        // Ease-out: it moves on the click's frame; a slow ease-in start reads as lag.
        const ease = (t) => 1 - ((1 - t) ** 3);
        let startTime = null;

        reviewRead.classList.remove('game-review-read-collapsed');
        reviewReadParagraph.style.setProperty('overflow', 'hidden');
        // Scroll anchoring would fight the manual scroll while the text above the notes shrinks.
        rootStyle.setProperty('overflow-anchor', 'none');

        const frame = (time) => {
            startTime ??= time;
            const progress = ease(Math.min((time - startTime) / duration, 1));
            reviewReadParagraph.style.setProperty('height', `${fromHeight + (collapsedHeight - fromHeight) * progress}px`);
            view.scrollTo({ top: fromScroll + (toScroll - fromScroll) * progress, behavior: 'instant' });
            if (progress < 1) {
                view.requestAnimationFrame(frame);
                return;
            }

            reviewReadParagraph.style.removeProperty('height');
            reviewReadParagraph.style.removeProperty('overflow');
            rootStyle.removeProperty('overflow-anchor');
            setExpandedState(false);
            resolve();
        };
        view.requestAnimationFrame(frame);
    });

    // A review shortened in edit mode, or a window widened while expanded, can leave nothing cut.
    const finishCollapse = () => {
        void syncCollapseTop();
        syncReadToggle();
        if (readToggle instanceof HTMLElement && readToggle.hidden && documentRef.activeElement === readToggle) {
            editButton?.focus();
        }
    };

    const toggleExpanded = async (expand) => {
        if (isTransitioning || isEditMode || !(reviewReadParagraph instanceof HTMLElement)) {
            return;
        }

        isTransitioning = true;
        const startHeight = reviewReadParagraph.getBoundingClientRect().height;
        if (expand) {
            collapsedHeight = startHeight;
        } else {
            // Collapsing from far below would leave the reader in the notes, with "Show more" off screen.
            const toggleBox = readToggle instanceof HTMLElement ? readToggle.getBoundingClientRect() : null;
            const headerOffset = readHeaderOffset();
            if (toggleBox !== null && toggleBox.top - (startHeight - collapsedHeight) < headerOffset) {
                // The expanded layout stays until the loop reads scrollY: collapsing first would shorten the
                // page and let the browser clamp the scroll position before the animation starts.
                if (canAnimateElement(reviewReadParagraph, documentRef.defaultView)) {
                    await collapseWithScroll(startHeight, headerOffset);
                } else {
                    setExpandedState(false);
                    reviewSection.scrollIntoView({ block: 'start', behavior: 'auto' });
                }

                isTransitioning = false;
                finishCollapse();
                return;
            }
        }

        setExpandedState(expand);
        if (!expand) {
            void syncCollapseTop();
        }

        if (canAnimateElement(reviewReadParagraph, documentRef.defaultView)) {
            const paragraphBox = reviewReadParagraph.getBoundingClientRect();
            // Only the part inside the viewport animates; the rest changes off screen. Animating the full
            // height of a long review fills the visible area in a couple of frames and reads as a jump.
            const visibleLimit = Math.max(
                Math.min(startHeight, paragraphBox.height),
                (documentRef.defaultView?.innerHeight ?? Infinity) - paragraphBox.top,
            );
            const fromHeight = Math.min(startHeight, visibleLimit);
            const toHeight = Math.min(paragraphBox.height, visibleLimit);
            // The clamp stays off while shrinking, so the height cuts the text and the ellipsis lands at the end.
            reviewRead.classList.remove('game-review-read-collapsed');
            reviewReadParagraph.style.setProperty('overflow', 'hidden');
            const animation = playAnimation(reviewReadParagraph, [
                { height: `${fromHeight}px` },
                { height: `${toHeight}px` },
            ], {
                duration: readCssTimeMs(
                    reviewComposer,
                    expand ? '--game-review-composer-open-duration' : '--game-review-composer-close-duration',
                    expand ? 320 : 220,
                ),
                easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
                fill: 'forwards',
            });
            await settleAnimations([animation]);
            setExpandedState(expand);
            cancelAnimations([animation]);
            reviewReadParagraph.style.removeProperty('overflow');
        }

        isTransitioning = false;
        if (expand) {
            void syncCollapseTop();
            return;
        }

        finishCollapse();
    };

    const applyViewMode = () => {
        isEditMode = false;
        stopKeyboardCloseReveal();
        reviewForm.hidden = true;

        const hasReview = confirmedContent !== '';
        syncReadModeContent();

        if (reviewRead instanceof HTMLElement) {
            reviewRead.hidden = !hasReview;
        }

        if (reviewEmpty instanceof HTMLElement) {
            reviewEmpty.hidden = hasReview;
        }

        if (editButton instanceof HTMLElement) {
            editButton.hidden = !hasReview;
        }

        syncReadToggle();
        void syncCollapseTop();
    };

    const applyEditMode = () => {
        isEditMode = true;
        startKeyboardCloseReveal();
        reviewForm.hidden = false;

        if (reviewRead instanceof HTMLElement) {
            reviewRead.hidden = true;
        }

        if (reviewEmpty instanceof HTMLElement) {
            reviewEmpty.hidden = true;
        }

        if (editButton instanceof HTMLElement) {
            editButton.hidden = true;
        }

        hideCollapseTop();
    };

    const resetMotionStyles = () => {
        reviewComposer.classList.remove('game-review-composer-morphing');
        reviewComposer.style.removeProperty('height');
    };

    // A form taller than the area between the bars keeps its bottom in view: `Done` and the caret line.
    const revealForm = () => {
        const windowRef = documentRef.defaultView;
        const root = documentRef.documentElement;
        if (!root || typeof windowRef?.getComputedStyle !== 'function') {
            return;
        }

        const delta = revealScrollDelta(
            reviewForm,
            root,
            root.clientHeight,
            (element) => windowRef.getComputedStyle(element),
            { oversized: 'end' },
        );
        if (delta !== 0) {
            windowRef.scrollBy({ top: delta, behavior: 'instant' });
        }
    };

    // On a phone the browser scrolls the page for the focused field once the on-screen keyboard opens, and
    // the page stays there when it closes, with `Done` under the bottom bar. The form is revealed again
    // when the visual viewport grows back by more than the address bar can (about 56 px); a keyboard is
    // hundreds. ponytail: a fixed threshold, not a keyboard API - a keyboard shorter than this, or a
    // floating one that never resizes the viewport, gets no second reveal.
    const KEYBOARD_CLOSE_GROWTH_PX = 150;
    let keyboardViewport = null;
    let viewportLowestHeight = 0;

    const onVisualViewportResize = () => {
        // Measured from the lowest height since the last growth: the keyboard closes over several resizes.
        const height = keyboardViewport.height;
        if (height - viewportLowestHeight <= KEYBOARD_CLOSE_GROWTH_PX) {
            viewportLowestHeight = Math.min(viewportLowestHeight, height);
            return;
        }

        viewportLowestHeight = height;
        if (documentRef.activeElement === reviewTextarea) {
            revealForm();
        }
    };

    const startKeyboardCloseReveal = () => {
        const viewport = documentRef.defaultView?.visualViewport;
        if (!viewport || keyboardViewport !== null) {
            return;
        }

        keyboardViewport = viewport;
        viewportLowestHeight = viewport.height;
        viewport.addEventListener('resize', onVisualViewportResize);
    };

    const stopKeyboardCloseReveal = () => {
        keyboardViewport?.removeEventListener('resize', onVisualViewportResize);
        keyboardViewport = null;
    };

    const focusMode = (editing) => {
        if (editing) {
            // The native reveal shows the field alone, so `Done` can end under the bottom bar, and Chrome
            // reveals a capped `field-sizing: content` field as if it were never capped. The field scrolls
            // itself to the caret at its end, then one page scroll reveals the whole form.
            reviewTextarea.focus({ preventScroll: true });
            reviewTextarea.setSelectionRange(reviewTextarea.value.length, reviewTextarea.value.length);
            reviewTextarea.scrollTop = reviewTextarea.scrollHeight;
            revealForm();
            return;
        }

        const hasReview = confirmedContent !== '';
        const target = hasReview ? editButton : reviewEmpty?.querySelector('[data-game-review-start]');
        target?.focus();
    };

    const transitionMode = async (editing) => {
        if (isTransitioning) {
            return;
        }

        const hasReview = confirmedContent !== '';
        const viewSurface = hasReview ? reviewRead : reviewEmpty;
        if (!(viewSurface instanceof HTMLElement) || !canAnimateElement(reviewComposer, documentRef.defaultView)) {
            editing ? applyEditMode() : applyViewMode();
            focusMode(editing);
            return;
        }

        isTransitioning = true;
        const startHeight = reviewComposer.getBoundingClientRect().height;
        // Held before its surfaces go absolute: measuring the end surface lays out the page, and a composer
        // collapsed to nothing for that layout clamps the page scroll on a page that ends below it.
        reviewComposer.style.setProperty('height', `${startHeight}px`);
        reviewForm.hidden = false;
        viewSurface.hidden = false;
        reviewComposer.classList.add('game-review-composer-morphing');
        if (!editing) {
            syncReadToggle();
        }
        const endSurface = editing ? reviewForm : viewSurface;
        const endHeight = endSurface.getBoundingClientRect().height;

        const duration = readCssTimeMs(
            reviewComposer,
            editing ? '--game-review-composer-open-duration' : '--game-review-composer-close-duration',
            editing ? 320 : 220,
        );
        const animations = [
            playAnimation(reviewComposer, [
                { height: `${startHeight}px` },
                { height: `${endHeight}px` },
            ], { duration, easing: MOTION_EASING, fill: 'forwards' }),
            playAnimation(viewSurface, [
                { opacity: editing ? 1 : 0, transform: `translateY(${editing ? 0 : -4}px)` },
                { opacity: editing ? 0 : 1, transform: `translateY(${editing ? -4 : 0}px)` },
            ], { duration: Math.min(duration, 220), easing: MOTION_EASING, fill: 'both' }),
            playAnimation(reviewForm, [
                { opacity: editing ? 0 : 1, transform: `translateY(${editing ? 8 : 0}px)` },
                { opacity: editing ? 1 : 0, transform: `translateY(${editing ? 0 : 8}px)` },
            ], { duration, easing: MOTION_EASING, fill: 'both' }),
        ].filter(Boolean);

        await settleAnimations(animations);
        editing ? applyEditMode() : applyViewMode();
        cancelAnimations(animations);
        resetMotionStyles();
        focusMode(editing);
        isTransitioning = false;
    };

    const openEditMode = async () => {
        if (isTransitioning || isEditMode) {
            return;
        }

        clearAutoSaveTimer();
        autosaveDueDuringSave = false;
        hasSavedThisEdit = false;
        lastSaveFailed = false;
        reviewTextarea.value = confirmedContent;
        syncSaveStatus();
        await transitionMode(true);
    };

    const setClosingState = (closing) => {
        isClosing = closing;
        reviewTextarea.readOnly = closing;
        if (!(doneButton instanceof HTMLElement)) {
            return;
        }

        if (closing) {
            doneButton.setAttribute('aria-disabled', 'true');
        } else {
            doneButton.removeAttribute('aria-disabled');
        }
    };

    // `Done` waits for the save in flight, saves what is left, and only then leaves edit mode. There is no
    // discard: autosave has already stored the earlier changes. A failed save keeps the edit open.
    const finishEditing = async () => {
        if (!isEditMode || isClosing || isTransitioning) {
            return;
        }

        setClosingState(true);
        clearAutoSaveTimer();
        autosaveDueDuringSave = false;

        let savedWhileClosing = false;
        if (activeSave !== null) {
            savedWhileClosing = await activeSave;
        }

        if (hasUnsavedChanges()) {
            savedWhileClosing = await saveCurrentText();
            if (!savedWhileClosing) {
                setClosingState(false);
                syncSaveStatus();
                // Back to the text, caret and selection where they were; the page stays where it is.
                reviewTextarea.focus({ preventScroll: true });
                return;
            }
        }

        if (savedWhileClosing) {
            await wait(SAVE_CONFIRMATION_MS);
        }

        reviewTextarea.value = confirmedContent;
        syncReadModeContent();
        syncUnloadGuard();
        await transitionMode(false);
        setClosingState(false);
        syncUnloadGuard();
    };

    startButtons.forEach((button) => {
        if (button instanceof HTMLElement) {
            button.addEventListener('click', openEditMode);
        }
    });

    if (editButton instanceof HTMLElement) {
        editButton.addEventListener('click', openEditMode);
    }

    reviewTextarea.addEventListener('input', () => {
        if (!isEditMode || isClosing) {
            return;
        }

        // Every input restarts the delay; whether anything is sent is decided when it runs out.
        scheduleAutosave();
        syncSaveStatus();
        syncUnloadGuard();
    });

    if (readToggle instanceof HTMLElement) {
        readToggle.addEventListener('click', () => toggleExpanded(!isExpanded));
    }

    if (collapseTopButton instanceof HTMLElement) {
        collapseTopButton.addEventListener('click', async () => {
            const hadFocus = documentRef.activeElement === collapseTopButton;
            await toggleExpanded(false);
            if (hadFocus && readToggle instanceof HTMLElement) {
                readToggle.focus({ preventScroll: true });
            }
        });
    }

    documentRef.defaultView?.addEventListener?.('resize', () => {
        if (isEditMode || isTransitioning) {
            return;
        }

        syncReadToggle();
        void syncCollapseTop();
    });

    // A web font swapping in after the first measure can move where the clamp cuts the text.
    documentRef.fonts?.ready?.then(() => {
        if (!isEditMode && !isTransitioning) {
            syncReadToggle();
        }
    });

    // Without JavaScript `Done` submits the form to the same route (redirect + flash).
    reviewForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        await finishEditing();
    });

    applyViewMode();

    return { openEditMode, finishEditing };
}

if (typeof document !== 'undefined') {
    initGameDetailsReview();
}
